
import {
  computed,
  defineComponent,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Gleap from 'gleap';
import StepShell from '@/components/onboarding-v2/shared/StepShell.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import {
  fetchMigrationV5Status,
  finalizeMigrationV5,
  MigrationV5Status,
} from '@/composables/migrationV5';

const POLL_INTERVAL_MS = 10_000;
// Soft "are you still here?" threshold (1 hour). Past this we surface a
// "contact support" CTA but keep polling — the admin script can take a
// while and we don't want to lock the user out if it overruns.
const SOFT_TIMEOUT_MS = 60 * 60 * 1000;
// Auto-redirect countdown after the migration completes. Mirrors the
// onboarding's LiftOffStep pattern (AUTO_REDIRECT_S = 10) so the user
// gets a beat to read the success state before the page jumps.
const AUTO_REDIRECT_S = 10;

type StageKey =
  | 'moduleConnected'
  | 'migration'
  | 'finalize';

interface Stage {
  key: StageKey;
  state: 'done' | 'active' | 'pending';
}

export default defineComponent({
  name: 'MigrationV5WaitStep',

  components: { StepShell, SpmButton },

  props: {
    canGoBack: { type: Boolean, default: false },
  },

  emits: ['next', 'back'],

  setup(_, { emit }) {
    const { t } = useI18n();

    // Default to `in_progress` so the stages render as "in flight" from
    // the very first paint. The state may transition through:
    //   in_progress → data_migrated → finalized
    // The wait step takes a different visual cue at each step:
    //   - in_progress : "Migration de votre boutique" active
    //   - data_migrated : "Finalisation" active (we kicked /finalize)
    //   - finalized : success screen + countdown + reload
    const status = ref<MigrationV5Status>('in_progress');
    const errored = ref(false);
    const startedAt = ref(Date.now());
    const elapsedMs = ref(0);

    // `ready` flips to true exactly once, on the poll tick that catches
    // status === 'finalized'. It drives the success screen + countdown
    // (mirrors LiftOffStep's `ready` ref).
    const ready = ref(false);
    const countdown = ref(AUTO_REDIRECT_S);
    const countdownPct = computed(
      () => Math.max(0, (countdown.value / AUTO_REDIRECT_S) * 100),
    );

    // Guards against re-calling /finalize on every 10s tick. Set to true
    // the moment we fire the call; reset only on /finalize failure so
    // the next tick can retry.
    let finalizeInFlight = false;

    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let tickTimer: ReturnType<typeof setInterval> | null = null;
    let countdownTimer: ReturnType<typeof setInterval> | null = null;

    /**
     * Stages timeline driven by the backend status:
     *   - in_progress      → migration active, finalize pending
     *   - data_migrated    → migration done, finalize active (we call
     *                        /finalize automatically in the background)
     *   - finalized        → all stages done; success screen takes over
     *
     * The mapping is deliberate: the user sees the "Finalisation" stage
     * actively spinning while the seeders run server-side, then it ticks
     * to done as the success screen appears.
     */
    const stages = computed<Stage[]>(() => {
      const allKeys: StageKey[] = [
        'moduleConnected',
        'migration',
        'finalize',
      ];
      const isMigrationDone =
        status.value === 'data_migrated' || status.value === 'finalized';
      const isFinalizeDone = status.value === 'finalized';
      return allKeys.map<Stage>((key) => {
        if (key === 'moduleConnected') return { key, state: 'done' };
        if (key === 'migration') {
          return { key, state: isMigrationDone ? 'done' : 'active' };
        }
        // finalize
        if (isFinalizeDone) return { key, state: 'done' };
        if (status.value === 'data_migrated') return { key, state: 'active' };
        return { key, state: 'pending' };
      });
    });

    const elapsedLabel = computed(() => {
      const totalSec = Math.floor(elapsedMs.value / 1000);
      const min = Math.floor(totalSec / 60);
      const sec = totalSec % 60;
      return min > 0 ? `${min} min ${sec}s` : `${sec}s`;
    });

    const redirectNow = () => {
      // The V5 stack must reinitialize from scratch (JWT may now resolve
      // V5, bundle_version=15, etc.). Hard-reload at `/` so the router
      // guard re-evaluates and lets the user through to the dashboard.
      window.location.replace('/');
    };

    const pollOnce = async () => {
      // Once we've entered the ready state, the only thing left is the
      // countdown — stop hitting the backend.
      if (ready.value) return;
      const next = await fetchMigrationV5Status();
      status.value = next;

      if (next === 'data_migrated' && !finalizeInFlight) {
        // The external migration script just signaled completion. Kick
        // the V5 seeders (default data source, system API key, VAPID,
        // default lists, V5 IDs cache) via POST /finalize. The status
        // flips to `finalized` on success and the next poll catches it.
        // On failure: reset the in-flight flag so the next 10s tick
        // retries — the backend is idempotent (each `ensure*` checks
        // first), so retries are safe.
        finalizeInFlight = true;
        try {
          await finalizeMigrationV5();
          // Don't read the response status here — let the next poll
          // tick own the transition to `finalized`, that keeps a single
          // path responsible for the success screen handover.
        } catch (e) {
          finalizeInFlight = false;
        }
        return;
      }

      if (next === 'finalized') {
        // Migration is officially done. Flip into the success screen
        // and let the watcher on `ready` start the 10s countdown. We
        // stop the poll/tick here (they'd be no-ops anyway with the
        // guards above, but keeping them running wastes wakeups).
        if (pollTimer) clearInterval(pollTimer);
        if (tickTimer) clearInterval(tickTimer);
        pollTimer = null;
        tickTimer = null;
        ready.value = true;
      }
    };

    // When `ready` flips to true, start the countdown — exactly like
    // LiftOffStep does on its own `ready` ref. Decrements every second;
    // on hitting 0 we redirect. The manual "Discover" CTA can also call
    // `redirectNow` directly to skip the wait.
    watch(ready, (value) => {
      if (!value) return;
      countdown.value = AUTO_REDIRECT_S;
      countdownTimer = setInterval(() => {
        countdown.value -= 1;
        if (countdown.value <= 0) {
          if (countdownTimer) clearInterval(countdownTimer);
          countdownTimer = null;
          redirectNow();
        }
      }, 1000);
    });

    const openSupport = () => {
      try {
        Gleap.open();
      } catch (e) {
        // noop
      }
    };

    onMounted(() => {
      // Stamp the "wait started at" so a refresh restarts the local clock
      // (this is fine — the duration is purely informational, the server
      // is the source of truth for the actual migration state).
      startedAt.value = Date.now();
      elapsedMs.value = 0;

      // Tick every second for the elapsed counter; soft-timeout flip after
      // 1h shows the "contact support" CTA (we keep polling regardless).
      tickTimer = setInterval(() => {
        elapsedMs.value = Date.now() - startedAt.value;
        if (!errored.value && elapsedMs.value >= SOFT_TIMEOUT_MS) {
          errored.value = true;
        }
      }, 1000);

      // First poll runs immediately to react fast if the admin script
      // happened to finish while the user was on step 1.
      pollOnce();
      pollTimer = setInterval(pollOnce, POLL_INTERVAL_MS);

      // Quietly satisfy emit binding (the wait step never emits anything;
      // back/next are wired by the parent but unused here).
      void emit;
    });

    onUnmounted(() => {
      if (pollTimer) clearInterval(pollTimer);
      if (tickTimer) clearInterval(tickTimer);
      if (countdownTimer) clearInterval(countdownTimer);
    });

    return {
      t,
      status,
      errored,
      ready,
      countdown,
      countdownPct,
      stages,
      elapsedLabel,
      openSupport,
      redirectNow,
    };
  },
});
